import { Injectable } from '@angular/core';
import {
  WorkspaceLinkPostBody,
  WorkspaceLinkPutBody,
} from 'generatedInterfaces';
import { LinksPresenter } from 'src/app/classes/presenters/linksPresenter';
import {
  CbCreateEditService,
  CbResolveReject,
} from 'src/app/widgets/cb-dialog-form/abstract/cb-create-edit.service';
import { CbDialogFormService } from 'src/app/widgets/cb-dialog-form/service/cb-dialog-form.service';
import { CbDialogService } from 'src/app/widgets/cb-dialog/cb-dialog-service/cb-dialog.service';
import { CbButtonType } from 'src/app/widgets/cb-form-widgets/cb-button/cb-button.component';
import { CbStandardForm } from 'src/app/widgets/cb-form/cb-form-interfaces';
import { CbToastService } from 'src/app/widgets/cb-toast/cb-toast.service';
import { createRows, editRows } from './create-edit-link.const';
import { lastValueFrom } from 'rxjs/internal/lastValueFrom';
import {
  WorkspaceLinkPostsBody,
  WorkspaceLinkPutsBody,
  WorkspaceLinkDeletesQuery,
} from 'generatedInterfaces';

@Injectable({
  providedIn: 'root',
})
export class CreateEditLinkService extends CbCreateEditService {
  public deleteTitle: string = 'LINKS.DELETE.TITLE';
  public deleteMessage: string = 'LINKS.DELETE.MESSAGE';
  public presenter: LinksPresenter = new LinksPresenter();

  constructor(
    public override readonly dialogService: CbDialogService,
    public override readonly dialogFormService: CbDialogFormService
  ) {
    super(dialogService, dialogFormService);
  }

  public beforeCreateOpen(data: any): void { }
  public beforeEditOpen(data: any): void { }

  public initCreateForm(): CbStandardForm {
    return {
      title: 'LINKS.CREATE',
      rows: createRows,
      submitButton: {
        label: 'COMMON.CREATE',
        type: CbButtonType.success,
        show: true,
        onClick: (values: { [key: string]: any }) => {
          super.create(values);
        },
      },
    };
  }

  public initEditForm(): CbStandardForm {
    return {
      title: 'LINKS.EDIT',
      rows: editRows,
      submitButton: {
        label: 'COMMON.SAVE',
        type: CbButtonType.success,
        show: true,
        onClick: (values: { [key: string]: any }) => {
          super.edit(values);
        },
      },
      deleteButton: {
        label: 'COMMON.DELETE',
        type: CbButtonType.danger,
        show: true,
        onClick: (values: { [key: string]: any }) => {
          super.delete(values['id']);
        },
      },
    };
  }

  public postValuesToBody(values: {
    [key: string]: any;
  }): WorkspaceLinkPostBody {
    return {
      id: this.getNewId(),
      title: values['title'],
      url: values['url'],
      keywords: values['keywords'],
      order: values['order'],
      createdAt: values['createdAt'],
      updatedAt: values['updatedAt'],
      deletedAt: values['deletedAt'],
    };
  }

  public putValuesToBody(values: { [key: string]: any }): WorkspaceLinkPutBody {
    return {
      id: values['id'],
      title: values['title'],
      url: values['url'],
      keywords: values['keywords'],
      order: values['order'],
      createdAt: values['createdAt'],
      updatedAt: values['updatedAt'],
      deletedAt: values['deletedAt'],
    };
  }

  public createCallback(
    values: { [key: string]: any },
    { resolve, reject }: CbResolveReject
  ): void {
    let postBody: WorkspaceLinkPostBody = this.postValuesToBody(values);
    this.presenter.postOneRequested(postBody).subscribe({
      next: (value) => {
        resolve(value);
      },
      error: (error) => {
        CbToastService.instance.showMessage({
          title: 'COMMON.ERROR',
          message: error.error.message,
          icon: 'alert-circle-sharp',
          type: 'danger',
        });
        reject(error);
      },
      complete: () => { },
    });
  }

  public editCallback(
    values: { [key: string]: any },
    { resolve, reject }: CbResolveReject
  ): void {
    let putBody: WorkspaceLinkPutBody = this.putValuesToBody(values);
    this.presenter.putOneRequested(putBody).subscribe({
      next: (value) => {
        resolve(value);
      },
      error: (error) => {
        CbToastService.instance.showMessage({
          title: 'COMMON.ERROR',
          message: error.error.message,
          icon: 'alert-circle-sharp',
          type: 'danger',
        });
        reject(error);
      },
      complete: () => { },
    });
  }

  public deleteCallback(
    id: string,
    { resolve, reject }: CbResolveReject
  ): void {
    this.presenter.deleteOneRequested({ id: id }).subscribe({
      next: (value) => {
        resolve(value);
      },
      error: (error) => {
        CbToastService.instance.showMessage({
          title: 'COMMON.ERROR',
          message: error.error.message,
          icon: 'alert-circle-sharp',
          type: 'danger',
        });
        reject(error);
      },
      complete: () => { },
    });
  }

  public async createManyLinks(
    elements: { [key: string]: any }[]
  ): Promise<void> {
    let bodies: WorkspaceLinkPostsBody = { bodies: [] };
    for (let values of elements) {
      let putBody: WorkspaceLinkPostsBody['bodies'][0] = {
        id: values['id'],
      };
      bodies.bodies.push(putBody);
    }

    try {
      await lastValueFrom(this.presenter.postsManyRequested(bodies));
      CbToastService.instance.showMessage({
        title: 'COMMON.SUCCESS',
        message: 'TRANSLATIONS.CREATE_MANY_SUCCESS',
        icon: 'checkmark-circle-outline',
        type: 'success',
      });
    } catch (error: any) {
      CbToastService.instance.showMessage({
        title: 'COMMON.ERROR',
        message: error && error.error ? error.error.message : 'Error',
        icon: 'alert-circle-sharp',
        type: 'danger',
      });
    }
  }

  public async editManyLinks(
    elements: { [key: string]: any }[]
  ): Promise<void> {
    let bodies: WorkspaceLinkPutsBody = { bodies: [] };
    for (let values of elements) {
      let putBody: WorkspaceLinkPutsBody['bodies'][0] = {
        id: values['id'],
      };
      bodies.bodies.push(putBody);
    }

    try {
      await lastValueFrom(this.presenter.putsManyRequested(bodies));
      CbToastService.instance.showMessage({
        title: 'COMMON.SUCCESS',
        message: 'TRANSLATIONS.EDIT_MANY_SUCCESS',
        icon: 'checkmark-circle-outline',
        type: 'success',
      });
    } catch (error: any) {
      CbToastService.instance.showMessage({
        title: 'COMMON.ERROR',
        message: error && error.error ? error.error.message : 'Error',
        icon: 'alert-circle-sharp',
        type: 'danger',
      });
    }
  }

  public async deleteManyLinks(ids: string[]): Promise<void> {
    let bodies: WorkspaceLinkDeletesQuery = { ids: [] };
    for (let id of ids) {
      bodies.ids.push(id);
    }

    try {
      await lastValueFrom(this.presenter.deletesManyRequested(bodies));
      CbToastService.instance.showMessage({
        title: 'COMMON.SUCCESS',
        message: 'TRANSLATIONS.DELETE_MANY_SUCCESS',
        icon: 'checkmark-circle-outline',
        type: 'success',
      });
    } catch (error: any) {
      CbToastService.instance.showMessage({
        title: 'COMMON.ERROR',
        message: error && error.error ? error.error.message : 'Error',
        icon: 'alert-circle-sharp',
        type: 'danger',
      });
    }
  }
}
